export const MAX_FILE_MB = 100
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

export type UnlockPdfErrorCode =
  | 'wrong_password'
  | 'unsupported_encryption'
  | 'invalid_pdf'
  | 'decrypt_error'

export class UnlockPdfError extends Error {
  readonly code: UnlockPdfErrorCode

  constructor(code: UnlockPdfErrorCode, message: string) {
    super(message)
    this.name = 'UnlockPdfError'
    this.code = code
  }
}

function classifyQpdfError(message: string): UnlockPdfErrorCode {
  if (/invalid password|incorrect password|bad password|password.*incorrect|check.*password/i.test(message)) {
    return 'wrong_password'
  }
  if (/unsupported.*encrypt|unsupported.*crypto|unknown encryption|unsupported.*pdf encryption/i.test(message)) {
    return 'unsupported_encryption'
  }
  if (/not a pdf|invalid pdf|damaged|corrupt|can't find|unable to find|xref/i.test(message)) {
    return 'invalid_pdf'
  }
  return 'decrypt_error'
}

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function unlockedPdfFilename(originalName: string): string {
  const dot = originalName.lastIndexOf('.')
  const base = dot !== -1 ? originalName.slice(0, dot) : originalName
  return `${base || 'document'}-bez-parolya.pdf`
}

export async function unlockPdf(file: File, password: string): Promise<Blob> {
  const { createQpdfRunner } = await import('qpdf-run')
  const runner = await createQpdfRunner({
    workerUrl: new URL('qpdf-run/worker', import.meta.url).href,
    qpdfJsUrl: new URL('qpdf-run/qpdf.js', import.meta.url).href,
    wasmUrl: new URL('qpdf-run/qpdf.wasm', import.meta.url).href,
    timeoutMs: 120_000,
  })

  try {
    const input = new Uint8Array(await file.arrayBuffer())
    const output = await runner.runOne({
      input,
      inputName: 'input.pdf',
      outputName: 'unlocked.pdf',
      args: [`--password=${password}`, '--decrypt', 'input.pdf', 'unlocked.pdf'],
    })

    if (!output.byteLength) {
      throw new UnlockPdfError('decrypt_error', 'qpdf produced an empty output file')
    }

    return new Blob([output], { type: 'application/pdf' })
  } catch (error) {
    if (error instanceof UnlockPdfError) throw error
    const message = error instanceof Error ? error.message : String(error)
    throw new UnlockPdfError(classifyQpdfError(message), message)
  } finally {
    await runner.destroy()
  }
}
